import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Droplets, BadgePercent, Loader2 } from "lucide-react";
import api from "@/lib/api";

// Tracked 20% lab-test offer landing — token-secured, no PII shown.
export default function TestOffer() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api.get(`/public/test-offer/${token}`)
      .then(({ data }) => { setOffer(data); setState("ready"); })
      .catch(() => setState("notfound"));
  }, [token]);

  const expiry = offer?.expires_at ? new Date(offer.expires_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center gap-2 font-bold text-slate-900 text-xl"><Droplets className="w-6 h-6 text-cyan-600" /> AWARE Water Testing</div>
        {state === "loading" && <div className="py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
        {state === "notfound" && (
          <div data-testid="offer-not-found" className="mt-8 bg-white rounded-3xl border border-slate-200 p-6 text-slate-700">
            This offer link isn't valid. You can still book laboratory water testing at any time.
            <Link to="/" className="mt-4 block font-bold text-cyan-700 underline">Book a water test</Link>
          </div>
        )}
        {offer && offer.status === "active" && (
          <div data-testid="offer-active" className="mt-8 bg-white rounded-3xl border border-slate-200 p-7 shadow-sm">
            <BadgePercent className="w-10 h-10 text-cyan-600 mx-auto" />
            <div className="mt-2 text-xs uppercase tracking-[0.2em] font-bold text-cyan-700">Water Health Check Thank-You Offer</div>
            <div className="mt-3 text-4xl font-bold text-slate-900">{offer.discount_percent}% OFF</div>
            <div className="mt-1 text-slate-600 text-sm">an AWARE laboratory water test for this property</div>
            <div className="mt-4 rounded-2xl bg-cyan-50 border border-cyan-200 p-4">
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Your single-use discount code</div>
              <div data-testid="offer-code" className="mt-1 text-2xl font-mono font-bold text-cyan-800">{offer.discount_code}</div>
              <div className="mt-1 text-xs text-slate-500">Applied at checkout · valid until <b>{expiry}</b></div>
            </div>
            <a data-testid="offer-book-cta" href={`/?discount=${offer.discount_code}`} className="mt-5 block w-full py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold">
              Book Your Laboratory Water Test – {offer.discount_percent}% Off
            </a>
            <div className="mt-3 text-[11px] text-slate-400">Choose the tank, bore or other package that suits your water supply — the discount applies with your code above.</div>
          </div>
        )}
        {offer && offer.status === "redeemed" && (
          <div data-testid="offer-redeemed" className="mt-8 bg-white rounded-3xl border border-emerald-200 p-6 text-slate-700">
            <div className="font-bold text-emerald-800">This offer has been redeemed — thank you!</div>
            <div className="text-sm mt-2">Your laboratory test booking is in progress. We'll be in touch with your results.</div>
          </div>
        )}
        {offer && offer.status === "expired" && (
          <div data-testid="offer-expired" className="mt-8 bg-white rounded-3xl border border-slate-200 p-6 text-slate-700">
            <div className="font-bold text-slate-900">This Water Health Check offer has expired</div>
            <div className="text-sm mt-2">You can still book laboratory water testing for this property.</div>
            <a data-testid="offer-expired-book" href="/" className="mt-4 inline-block px-6 py-3 rounded-full bg-slate-900 text-white font-bold text-sm">BOOK WATER TEST</a>
          </div>
        )}
      </div>
    </div>
  );
}
